import { useEffect, useState, useCallback } from '@biletiniz/ui-core';
import { useLazyQuery } from '@biletiniz/apollo';
import { useRouter } from 'next/router';
import { qs } from '@biletiniz/shared';
import { ALL_FLIGHT_OFFERS_QUERY } from './gql';
import {
  getLastFlightSearchSession,
  shouldRenewFlightSearchSession,
  saveFlightSearchSession,
} from '../flight-search-session';

const getSearchFlightOfferVariable = search => {
  return {
    departureDate: search?.departureDate,
    returnDate: search?.returnDate,
    origin: search?.origin?.value,
    originIsCity: search?.origin?.isCity,
    originCountry: search?.origin?.countryCode,
    destination: search?.destination?.value,
    destinationIsCity: search?.destination?.isCity,
    destinationCountry: search?.destination?.countryCode,
    adultsCount: search?.cabinClassTravellers?.travellers?.adultsCount,
    childrenCount: search?.cabinClassTravellers?.travellers?.childrenCount,
    infantsCount: search?.cabinClassTravellers?.travellers?.infantsCount,
    cabinClass: search?.cabinClassTravellers?.cabinClass,
    nonStop: false, // TODO get it from ui in the future
  };
};

const getFilterFlightOfferVariable = filter => {
  return {
    stopsNumber: filter.stopsNumber,
    airlines: filter.airlines,
    fareTypes: filter.fareTypes,
    flightNumber: filter.flightNumber,
    transitAirports: filter.transitAirports,
    directions: filter.directions,
  };
};

export const useInfiniteFlightOffers = (search, filter, pageSize = 20) => {
  const [loaded, setLoaded] = useState(false);
  const [session, setSession] = useState(null);
  // Changes with every search response, including a renewed session that
  // kept its id, so the countdown can restart from it.
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [allFlightOffers, setAllFlightOffers] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Store metadata and viewer map from initial search - these persist during pagination
  const [metadata, setMetadata] = useState(null);
  const [flightOffersViewerMap, setFlightOffersViewerMap] = useState(null);
  const router = useRouter();
  const searchWithTripType = {
    ...search,
    tripType: search.returnDate ? 'twoWay' : 'oneWay',
  };

  const [loadFlightOffers, { data, error, loading, ...rest }] = useLazyQuery(
    ALL_FLIGHT_OFFERS_QUERY,
    {
      fetchPolicy: `network-only`,
    }
  );
  let fromCheckoutPage;

  // Initial load
  useEffect(() => {
    const flightSearchFilter = getSearchFlightOfferVariable(search);
    const flightOfferFilter = getFilterFlightOfferVariable(filter);
    fromCheckoutPage = sessionStorage.getItem('from-checkout-page');
    if (fromCheckoutPage) {
      sessionStorage.removeItem('from-checkout-page');
    }
    const cleanQuery = qs.stringify(searchWithTripType);
    const currentQuery = router.asPath.split('?')[1] || '';
    // Back from checkout the filters are dropped by moving to the clean search
    // URL; the URL change runs this effect again and loads. When the page is
    // already on that URL (back from checkout a second time) the push changes
    // nothing and this effect would never run again, leaving the loader open
    // with no request sent, so load right away instead.
    if (
      fromCheckoutPage &&
      JSON.stringify(qs.parse(currentQuery)) !==
        JSON.stringify(qs.parse(cleanQuery))
    ) {
      router.push(`/ucak-seferler?${cleanQuery}`);
    } else if (
      flightSearchFilter.departureDate &&
      flightSearchFilter.origin &&
      flightSearchFilter.destination
    ) {
      setCurrentPage(1);
      setAllFlightOffers([]); // Clear old offers
      setHasMore(true);
      // setLoaded(false); // Reset loaded state

      loadFlightOffers({
        variables: {
          flightSearchFilter,
          flightOfferFilter,
          // The last session always goes along so the API can delete its data
          // when this search replaces it. Same search (filters, back from
          // checkout, reload) keeps it and its countdown.
          session: getLastFlightSearchSession(),
          renewSession: shouldRenewFlightSearchSession(search),
          page: 1,
          pageSize,
        },
      });
    }
  }, [search, filter]); // Remove pageSize from dependencies

  // Handle data updates
  useEffect(() => {
    if (data?.flightSearch) {
      const searchResults = data.flightSearch;

      if (searchResults.session) {
        setSession(searchResults.session);
        setSessionExpiresAt(
          saveFlightSearchSession({
            session: searchResults.session,
            search,
            remainingSeconds: searchResults.sessionTTL,
          })
        );
      }

      // Store metadata and viewer map only from page 1 (initial search/filter)
      if (
        searchResults.pagination?.currentPage === 1 ||
        !searchResults.pagination
      ) {
        if (searchResults.metadata) {
          setMetadata(searchResults.metadata);
        }
        if (searchResults.flightOffersViewerMap) {
          setFlightOffersViewerMap(searchResults.flightOffersViewerMap);
        }
      }

      if (searchResults.pagination) {
        setHasMore(searchResults.pagination.hasMore);

        if (searchResults.pagination.currentPage === 1) {
          // First page - replace all offers (fresh search or filter)

          setAllFlightOffers(searchResults.flightOffers || []);
        } else {
          // Subsequent pages - append offers (load more)
          setTimeout(() => {
            setAllFlightOffers(prev => [
              ...prev,
              ...(searchResults.flightOffers || []),
            ]);
          }, 100);
        }
      } else {
        setAllFlightOffers(searchResults.flightOffers || []);
      }
    }
  }, [data]);

  // Handle loading state for load more
  // eslint-disable-next-line
  useEffect(() => {
    if (isLoadingMore) {
      // Wait 100ms before checking loading state to give Apollo time to start
      const timer = setTimeout(() => {
        if (!loading) {
          // Wait 500ms before hiding the loading state
          setTimeout(() => {
            setIsLoadingMore(false);
          }, 500);
        }
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [loading, isLoadingMore]);

  useEffect(() => {
    if ((data || error) && !loaded) {
      setLoaded(true);
    }
  }, [data, error]);

  // Load more function for infinite scroll
  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore || loading) return;

    const flightSearchFilter = getSearchFlightOfferVariable(search);
    const flightOfferFilter = getFilterFlightOfferVariable(filter);

    if (
      flightSearchFilter.departureDate &&
      flightSearchFilter.origin &&
      flightSearchFilter.destination
    ) {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);

      loadFlightOffers({
        variables: {
          flightSearchFilter,
          flightOfferFilter,
          session, // Use the session from the previous response
          page: nextPage,
          pageSize,
        },
      });
    }
  }, [
    hasMore,
    isLoadingMore,
    loading,
    currentPage,
    search,
    filter,
    pageSize,
    session,
  ]);

  return {
    session,
    sessionExpiresAt,
    metadata, // Use stored metadata (preserved from initial search)
    flightOffersViewerMap, // Use stored viewer map (preserved from initial search)
    flightOffers: allFlightOffers,
    pagination: data?.flightSearch?.pagination,
    loadFlightOfferError: error?.message,
    isLoadingFlightOffer: loading && currentPage === 1, // Only true for initial load
    isLoadingMore,
    loadedFlightOffer: loaded,
    previousFlightOffers: rest.previousData,
    loadMore,
    fromCheckoutPage,
  };
};
